<script>
import Vue from 'vue';
import { Event } from '@/models/Event';
import { LocationCollection } from '@/models/Location';
import { formRules } from '@/helpers/tools';
import { AddressCollection } from '@/models/Address';
import CountrySelectField from '@/components/country/SelectField.vue';
import UrlField from '@/components/form/UrlField.vue';

export default Vue.extend({
    name: 'CompactViewLocationEdit',
    components: {
        CountrySelectField,
        UrlField,
    },
    props: {
        eventRecord: Event,
    },
    data() {
        return {
            loading: false,
            locationRec: null,
            addressRec: null,
        };
    },
    computed: {
        location_id() {
            return this.eventRecord?.location_id || null;
        },
    },
    watch: {
        location_id(newId, oldId) {
            if (newId !== oldId) {
                this.loadLocation(newId || null);
            }
        },
    },
    created() {
        this.formRules = formRules;
    },
    mounted() {
        this.loadLocation(this.eventRecord?.location_id || null);
    },
    methods: {
        async loadLocation(locationId) {
            if (!locationId) {
                this.locationRec = null;
                return;
            }
            this.loading = true;
            const locCollection = new LocationCollection();
            const addrCollection = new AddressCollection();

            await locCollection.query({ id: locationId });
            const loc = locCollection.first();
            this.locationRec = loc;

            await addrCollection.query({ id: loc?.address_id });
            const addr = addrCollection.first();
            this.addressRec = addr;

            this.loading = false;
        },
        onLocationValueChange(field, newVal) {
            if (this.locationRec) {
                if (newVal !== this.locationRec.get(field)) {
                    this.locationRec.set(field, newVal);
                    this.saveRecs();
                }
            }
        },
        onAddressValueChange(field, newVal) {
            if (this.addressRec) {
                if (newVal !== this.addressRec.get(field)) {
                    this.addressRec.set(field, newVal);
                    this.saveRecs();
                }
            }
        },

        async saveRecs() {
            try {
                this.loading = true;
                if (this.locationRec && this.locationRec.isDirty()) {
                    await this.locationRec.save();
                }
                if (this.addressRec && this.addressRec.isDirty()) {
                    await this.addressRec.save();
                }
                this.$emit('saved', this.locationRec, this.addressRec);
            } finally {
                this.loading = false;
            }
        },
    },
});
</script>

<template>
    <div>
        <h3 class="section-heading">{{ $t('location') }}</h3>
        <div v-if="locationRec && addressRec">
            <div class="d-flex mb-2 gap-5">
                <div class="flex-equal">
                    <LabelField :label="$t('name')">
                        <v-text-field
                            :value="addressRec.title"
                            :disabled="loading"
                            dense
                            hide-details="auto"
                            outlined
                            clearable
                            :rules="[formRules.required]"
                            @change="onAddressValueChange('title', $event)"
                        />
                    </LabelField>
                </div>
                <div class="flex-equal">
                    <LabelField :label="$t('print_title')">
                        <v-text-field
                            :value="addressRec.print_title"
                            :disabled="loading"
                            dense
                            hide-details="auto"
                            outlined
                            clearable
                            @change="onAddressValueChange('print_title', $event)"
                        />
                    </LabelField>
                </div>
            </div>

            <div class="d-flex mb-2 gap-5">
                <div class="flex-equal">
                    <LabelField :label="$t('strasse')">
                        <v-text-field
                            :value="addressRec.street"
                            :disabled="loading"
                            dense
                            hide-details="auto"
                            outlined
                            clearable
                            @change="onAddressValueChange('street', $event)"
                        />
                    </LabelField>
                </div>
                <div class="flex-equal">
                    <LabelField :label="$t('strasse_nr')">
                        <v-text-field
                            :value="addressRec.street_nr"
                            :disabled="loading"
                            dense
                            hide-details="auto"
                            outlined
                            clearable
                            @change="onAddressValueChange('street_nr', $event)"
                        />
                    </LabelField>
                </div>
            </div>

            <div class="d-flex mb-2 gap-5">
                <div class="flex-equal">
                    <LabelField :label="$t('zip')">
                        <v-text-field
                            :value="addressRec.zip"
                            :disabled="loading"
                            dense
                            hide-details="auto"
                            outlined
                            clearable
                            @change="onAddressValueChange('zip', $event)"
                        />
                    </LabelField>
                </div>
                <div class="flex-equal">
                    <LabelField :label="$t('city')">
                        <v-text-field
                            :value="addressRec.city"
                            :disabled="loading"
                            dense
                            hide-details="auto"
                            outlined
                            clearable
                            @change="onAddressValueChange('city', $event)"
                        />
                    </LabelField>
                </div>
            </div>

            <div class="d-flex mb-2 gap-5">
                <div class="flex-equal">
                    <LabelField :label="$t('land')">
                        <CountrySelectField
                            :value="addressRec.country_id"
                            hide-details="auto"
                            item-value="id"
                            :label="null"
                            @input="onAddressValueChange('country_id', $event)"
                        />
                    </LabelField>
                </div>
                <div class="flex-equal">
                    <div class="d-flex gap-5">
                        <LabelField class="flex-equal" :label="$t('latitude')">
                            <v-text-field
                                :value="addressRec.coord_lat"
                                :disabled="loading"
                                dense
                                hide-details="auto"
                                outlined
                                clearable
                                @change="onAddressValueChange('coord_lat', $event)"
                            />
                        </LabelField>
                        <LabelField class="flex-equal" :label="$t('longitude')">
                            <v-text-field
                                :value="addressRec.coord_lon"
                                :disabled="loading"
                                dense
                                hide-details="auto"
                                outlined
                                clearable
                                @change="onAddressValueChange('coord_lon', $event)"
                            />
                        </LabelField>
                    </div>
                </div>
            </div>

            <h4>{{ $t('contact') }}</h4>
            <div class="d-flex mb-2 gap-5">
                <div class="flex-equal">
                    <LabelField :label="$t('webseite')">
                        <UrlField
                            :value="addressRec.web"
                            :disabled="loading"
                            dense
                            hide-details="auto"
                            outlined
                            clearable
                            @change="onAddressValueChange('web', $event)"
                        />
                    </LabelField>
                </div>
                <div class="flex-equal">
                    <LabelField class="flex-equal" :label="$t('email')">
                        <v-text-field
                            :value="addressRec.email"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[formRules.email, formRules.maxLength(255)]"
                            @change="onAddressValueChange('email', $event)"
                        />
                    </LabelField>
                </div>
            </div>

            <div class="d-flex mb-2 gap-5">
                <div class="flex-equal">
                    <LabelField :label="$t('tel')">
                        <v-text-field
                            :value="addressRec.tel"
                            dense
                            outlined
                            hide-details="auto"
                            @change="onAddressValueChange('tel', $event)"
                        />
                    </LabelField>
                </div>
                <div class="flex-equal">
                    <LabelField class="flex-equal" :label="$t('mobile')">
                        <v-text-field
                            :value="addressRec.mobile"
                            dense
                            outlined
                            hide-details="auto"
                            @change="onAddressValueChange('mobile', $event)"
                        />
                    </LabelField>
                </div>
            </div>
        </div>
        <p v-else>Keine Lokation gewählt.</p>
    </div>
</template>
